import SUBSCRIPTION, {
    ConsumptionExtraType,
    PurchaseOrigin,
} from '@powerednow/shared/constants/subscription';
import { StoreUtils } from '@powerednow/app/FieldServices/singletons/StoreUtils';

import 'cordova-plugin-purchase';

// Why do I need to ignore the next import? The compiler warns about module has no exported member 'ConsumptionExtraType'. It works tho
// @ts-ignore

declare let TrackingManager: any;
declare let SyncManager: any;
declare let Ext: any;

export interface IExtraStoreProductData {
    priceDecimal: number;
    currency: string;
    description: string;
    product_id: string;
    introPriceFormatted: string;
}

export interface IPoweredNowPurchaseRecord {
    product: string;
    transactionid: string;
    receipt: string;
    order_id: string;
    quantity: number;
    original_transaction_id: string;
    transaction_id: string;
    expires_date: string;
    purchase_date: string;
    original_purchase_date: string;
    purchase_origin: PurchaseOrigin;
    purchase_currency: string;
    purchase_amount: number;
    original_price: string;
    dt_created: string;
}

export interface IPoweredNowConsmuptionExtraRecord {
    type: ConsumptionExtraType;
    count: number;
    dt_created: string;
    product: string;
    receipt: string;
    quantity: number;
    order_id: string;
    transaction_id: string;
    purchase_date: string;
    expires_date: string;
    isverified: number;
    purchase_origin: number;
    purchase_currency: string;
    purchase_amount: number;
    original_price: string;
}

export type EnrichedStoreProduct<T> = {
    [P in keyof (T & IExtraStoreProductData)]: (T & IExtraStoreProductData)[P];
};

export abstract class PurchasePluginBridge {
    protected store: CdvPurchase.Store;

    protected static get platform(): CdvPurchase.Platform {
        throw new Error('Must implement in subclass!');
    }

    declare ['constructor']: typeof PurchasePluginBridge;

    protected constructor(storeInterface: CdvPurchase.Store) {
        this.store = storeInterface;
        this.store.verbosity = 4; // Set to 4 if you want detailed purchase debug info
        this.store.validator = `${StoreUtils.API_BASE_URL}interfaces/purchase-verifier-v2`;
        /**
         * This is an external validator service, which is works for sure. It is free for up to 100 validations per month
         * pretty handy to test and helps mimick the required behaviour for validator interface.
         */
        // this.store.validator = 'https://validator.fovea.cc/v1/validate?appName=com.powerednow.fieldservices&apiKey=b3614f4f-e91a-493f-9697-0b457d3572b6';
    }

    public async registerProduct(productsDefinition: CdvPurchase.IRegisterProduct | CdvPurchase.IRegisterProduct[]): Promise<CdvPurchase.Product[]> {
        const productsDefinitions = Array.isArray(productsDefinition) ? productsDefinition : [productsDefinition];
        const productsDefinitionsWithPlatform = productsDefinitions.map(productsDefinition => ({
            ...productsDefinition,
            platform: this.constructor.platform,
        }));
        this.store
            .when()
            .productUpdated(product => {
                this.fireSubscriptionProductUpdatedEvent(product.id);
            });
        this.store.register(productsDefinitionsWithPlatform);
        this.startWatchingProducts();
        // @ts-ignore
        await this.store.initialize([{
            platform: this.constructor.platform,
            options: {
                autoFinish: localStorage.getItem('autoFinishIAP') ? true : undefined,
            },
        }]);
        return this.store.products;
    }

    private fireSubscriptionProductUpdatedEvent(productId) {
        Ext.fireEvent('subscriptionProductUpdated', productId);
    }

    public getProduct(productIdOrAlias): EnrichedStoreProduct<CdvPurchase.Product> {
        const product = this.store.get(productIdOrAlias);
        return PurchasePluginBridge.enrichProductData(product);
    }

    public async buyProduct(productIdOrAlias) {
        const resolvingPromise = new Promise((resolve, reject) => {
            const removeListeners = () => {
                this.store.off(onFinished);
                this.store.off(onError);
            };
            const onFinished = (transaction: CdvPurchase.Transaction) => {
                resolve(transaction);
                removeListeners();
            };

            const onError = () => {
                reject();
                removeListeners();
            };
            this.store.when()
                .finished(onFinished);
        });
        const product = this.store.get(productIdOrAlias);
        if (product.type === 'consumable' && !product.canPurchase) {
            resolvingPromise.then((transaction: CdvPurchase.Transaction) => {
                transaction.finish();
            });
        } else {
            const offer = product.getOffer();
            await this.store.order(offer);
        }
        return resolvingPromise;
    }

    public restoreProducts() {
        this.startWatchingProducts();
        this.store.restorePurchases();
    }

    startWatchingProducts() {
        this.store.when()
            .approved(this.onProductApproved.bind(this))
            .verified(this.onProductVerified.bind(this))
            .finished(this.onProductFinished.bind(this));
    }

    /**
     * This called when the user finished the client side interactions with a purchase, the OS already asked for password,
     * confirmation and everything went well. At this point the client thinks someone has bought the product and we can
     * remotely verify the receipt we've got.
     * @param product
     */
    protected async onProductApproved(transaction: CdvPurchase.Transaction) {
        console.log('onProductApproved', transaction);
        const verifyRequired = await this.isVerifyRequired(transaction);
        console.log('verify required', verifyRequired, transaction);
        if (verifyRequired) {
            transaction.verify();
        } else {
            transaction.finish();
        }
    }

    protected async onProductVerified(receipt: CdvPurchase.VerifiedReceipt) {
        console.log('onProductVerified', receipt);
        const [product] = receipt.sourceReceipt.lastTransaction().products;

        const storeProduct = this.store.get(product.id);
        if (storeProduct) {
            /**
             * No such check as for subscriptions, as consumables arrives as APP PRODUCT receipt.
             */
            if (storeProduct.type === 'consumable') {
                await this.onConsumableVerified(receipt);
                PurchasePluginBridge.reportProductPurchased(storeProduct, receipt.sourceReceipt.lastTransaction());
            }
            /**
             * For some reason expired subs can appear here as app receipt, so lets check if the store product matches
             * with the product in the receipt
             */
            if (product.id === receipt.id && storeProduct.type === 'paid subscription') {
                await this.onSubscriptionVerified(receipt);
                PurchasePluginBridge.reportProductPurchased(storeProduct, receipt.sourceReceipt.lastTransaction());
            }
        }
        // Lets clean up everything which we couldn't handle
        receipt.sourceReceipt.transactions.forEach(transaction => transaction.finish());
    }

    protected async onProductFinished(product: CdvPurchase.Product) {
        console.log('onProductFinished', product);
    }

    protected async isVerifyRequired(transaction: CdvPurchase.Transaction): Promise<Boolean> {
        /**
         * If we are not logged in for any reason, e.g. initiated a purchase and logged out, or bug in the code, do not
         * risk anything, ask the plugin to do a remote verify. We won't `finish()` our purchase transaction, so it
         * will automatically retries the process, even if everything goes wrong, the restore purchases button still
         * returns the purchases which are rechecked every time.
         */
        if (!SyncManager.getOnlineSyncEnabled()) {
            return true;
        }
        /**
         * The only information we have on client side when the product is approved is a transaction id( and the `appStoreReceipt`, but we can't use that).
         * If we have any record in Purchases store where this id can be found, then we already have some purchase record saved,
         * renewals done on server side. We can finish this ongoing purchase transaction.
         */

        for (const product of transaction.products) {
            const storeProduct = this.store.get(product.id);
            if (storeProduct && storeProduct.type === CdvPurchase.ProductType.CONSUMABLE || !this.hasExistingPurchaseRecordForTransaction(transaction)) {
                return true;
            }
        }
        return false;
    }

    protected abstract hasExistingPurchaseRecordForTransaction(product: CdvPurchase.Transaction): Boolean;

    protected getOriginalPurchaseRecordForReceipt(purchaseRecord: IPoweredNowPurchaseRecord): IPoweredNowPurchaseRecord {
        const purchasesStore = Ext.getStore('Purchases');
        return purchasesStore.findRecordByFilterFn((record: { data: IPoweredNowPurchaseRecord }) => {
            if ((record.data.original_transaction_id == purchaseRecord.original_transaction_id
                    && record.data.product === purchaseRecord.product
                    && record.data.expires_date === purchaseRecord.expires_date)
                || (String(record.data.order_id) === String(purchaseRecord.order_id))
            ) {
                return true;
            }
        });
    }

    private getOriginalConsumptionExtraRecord(purchaseRecord: IPoweredNowConsmuptionExtraRecord): IPoweredNowPurchaseRecord {
        const purchasesStore = Ext.getStore('ConsumptionExtra');
        return purchasesStore.findRecordByFilterFn((record: { data: IPoweredNowConsmuptionExtraRecord }) => {
            if ((
                record.data.product === purchaseRecord.product
                    && record.data.expires_date
                    && record.data.expires_date === purchaseRecord.expires_date)
                || (record.data.transaction_id === purchaseRecord.transaction_id)
            ) {
                return true;
            }
        });
    }

    protected abstract getPoweredNowPurchaseRecord(productData: CdvPurchase.VerifiedReceipt): IPoweredNowPurchaseRecord | null;

    protected abstract getPoweredNowConsumptionExtraRecord(productData: CdvPurchase.VerifiedReceipt): IPoweredNowConsmuptionExtraRecord | null;

    protected async onSubscriptionVerified(verifiedReceipt: CdvPurchase.VerifiedReceipt) {
        const poweredNowPurchaseRecord = this.getPoweredNowPurchaseRecord(verifiedReceipt);
        const originalPurchaseRecord = this.getOriginalPurchaseRecordForReceipt(poweredNowPurchaseRecord);

        console.log('original record', originalPurchaseRecord);
        if (poweredNowPurchaseRecord && !originalPurchaseRecord) {
            const purchasesStore = await StoreUtils.getStoreAndLoad('Purchases');
            purchasesStore.add(poweredNowPurchaseRecord);
            purchasesStore.sync();
        }
    }

    protected async onConsumableVerified(product: CdvPurchase.VerifiedReceipt) {
        const consumptionExtraRecord = this.getPoweredNowConsumptionExtraRecord(product);
        const originalConsumptionExtraRecord = this.getOriginalConsumptionExtraRecord(consumptionExtraRecord);
        console.log('original consumption record', originalConsumptionExtraRecord, product);
        if (!originalConsumptionExtraRecord) {
            const purchasesStore = await StoreUtils.getStoreAndLoad('ConsumptionExtra');
            purchasesStore.add(consumptionExtraRecord);
            purchasesStore.sync();
            Ext.Viewport.fireEvent('consumablePurchased');
        }
    }

    protected getConsumptionExtraType(cdvProduct: CdvPurchase.Product): ConsumptionExtraType {
        const PRODUCTS = SUBSCRIPTION.PRODUCT;
        return Object.values(PRODUCTS).find(product => [product?.id?.android, product?.id?.ios].includes(cdvProduct.id))?.type;
    }

    protected static enrichProductData<T extends CdvPurchase.Product>(productData: T): EnrichedStoreProduct<T> {
        const extraData: IExtraStoreProductData = {
            currency: productData.pricing.currency,
            priceDecimal: productData.pricing.priceMicros / 10 ** 6,
            description: productData.description,
            product_id: productData.id,
            introPriceFormatted: null,
        };
        productData.pricing.price = productData.pricing.price ? String(productData.pricing.priceMicros) : null; // SymbolizePrice productData.currency,

        return { ...productData, ...extraData };
    }

    private static reportProductPurchased(product: CdvPurchase.Product, transaction: CdvPurchase.Transaction) {
        TrackingManager.trackEvent('Subscription', 'new purchase recorded', product.id, {
            _valueToSum: product.pricing.price,
            fb_currency: product.pricing.currency,
            fb_num_items: 1,
            fb_description: product.description,
            'Content ID': product.id,
            // 'Product Title': productName, // Need PurchaseSetup store ?:(
            'Transaction Identifier': transaction.transactionId,
        });
        TrackingManager.initGoogleAppConversionTracker(TrackingManager.GAPPT_EVENT_SUBSCRIBE);
    }

    protected getPurchaseSetupRecordForProduct(product: CdvPurchase.Product): any {
        return Ext.getStore('PurchaseSetup').getRecordByProductId(product.id);
    }
}
